// Een selectie inspiratievideo's klaarzetten voor een klant.
//
// Joris, 11 sep 2026: "klant mag link doorsturen en kijken. En soms downloaden.
// Dat bij een instelling." De vervaltermijn is het slot: standaard twee weken,
// hoogstens drie maanden (dezelfde termijnen als de Beeldbank, zonder
// "onbeperkt"). Versturen doe je zelf — wij zetten de link klaar.
import { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, MenuItem, Switch, TextField, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const API = `${AI_ENGINE_URL}/api/v1/inspiration`;

export default function DeelDialog({ videoIds, onClose, verzameling = null }) {
  const [vorm, setVorm] = useState({ titel: verzameling?.naam || '', intro: '', dagen: 14, magDownloaden: false });
  const [bezig, setBezig] = useState(false);
  const [resultaat, setResultaat] = useState(null);

  const maak = async () => {
    setBezig(true);
    try {
      const r = await authFetch(`${API}/shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...vorm, videoIds, collectionId: verzameling?.id || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
      setResultaat(d);
    } catch (e) {
      toast.error(`Link maken mislukt: ${e.message}`);
    } finally { setBezig(false); }
  };

  const kopieer = async (tekst) => {
    try { await navigator.clipboard.writeText(tekst); toast.success('Link gekopieerd'); }
    catch { toast.error('Kopiëren lukte niet — selecteer de link handmatig'); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {resultaat ? 'De link staat klaar' : verzameling ? `Deel verzameling "${verzameling.naam}"` : `Deel ${videoIds.length} video${videoIds.length === 1 ? '' : "'s"} met een klant`}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {resultaat ? (
          <>
            <TextField size="small" value={resultaat.share.link} fullWidth
              InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
            <Button variant="contained" startIcon={<ContentCopyIcon />}
              onClick={() => kopieer(resultaat.share.link)} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              Link kopiëren
            </Button>
            <Typography variant="body2" color="text.secondary">
              Werkt tot {new Date(resultaat.share.verlooptOp).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}.
              Doorsturen mag. {resultaat.share.magDownloaden ? 'Downloaden staat aan.' : 'Downloaden staat uit.'}
            </Typography>
            {resultaat.overgeslagen?.length > 0 && (
              <Alert severity="warning">
                {resultaat.overgeslagen.length} video{resultaat.overgeslagen.length === 1 ? '' : "'s"} staan
                niet in onze eigen opslag en zitten er niet in. Upload ze eerst als je ze wilt meesturen.
              </Alert>
            )}
          </>
        ) : (
          <>
            <TextField size="small" label="Titel voor de klant" value={vorm.titel}
              placeholder="Bijvoorbeeld: Referenties voor de campagne"
              onChange={(e) => setVorm((f) => ({ ...f, titel: e.target.value }))} />
            <TextField size="small" label="Korte introductie (optioneel)" multiline minRows={2}
              value={vorm.intro} onChange={(e) => setVorm((f) => ({ ...f, intro: e.target.value }))} />
            <TextField select size="small" label="Hoe lang geldig" value={vorm.dagen}
              onChange={(e) => setVorm((f) => ({ ...f, dagen: Number(e.target.value) }))}>
              {[[7, '1 week'], [14, '2 weken'], [30, '1 maand'], [90, '3 maanden']].map(([d, naam]) => (
                <MenuItem key={d} value={d}>{naam}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Switch checked={vorm.magDownloaden}
                onChange={(e) => setVorm((f) => ({ ...f, magDownloaden: e.target.checked }))} />}
              label="Klant mag de video's downloaden"
            />
            {verzameling && (
              <Alert severity="info">
                Deze link groeit mee: video's die je later aan "{verzameling.naam}" toevoegt, ziet de klant ook.
                Haal je er iets uit, dan verdwijnt het ook uit de link.
              </Alert>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Alleen video's uit onze eigen opslag gaan mee; een YouTube-link is toch al openbaar.
                De link is niet te raden en verloopt vanzelf, maar wie hem heeft kan kijken —
                doorsturen kun je niet tegenhouden.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {resultaat ? 'Sluiten' : 'Annuleren'}
        </Button>
        {!resultaat && (
          <Button variant="contained" onClick={maak} disabled={bezig} sx={{ textTransform: 'none' }}>
            {bezig ? <CircularProgress size={18} /> : 'Link maken'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
